/**
 * The task event schema (NG-2056, ruled on NG-2054), shared by every surface
 * that writes a task: the issue store, and the capacity grid, which calls the
 * work-item service directly. One module keeps the events identical whichever
 * surface fires them.
 */
import { type AnalyticsSource, track } from "@/lib/analytics";
import type { TIssue } from "@/types/issue";

/**
 * The keys a task write actually changes, measured against the row as it was
 * before the write. A key the cache has no row for counts as changed. Values
 * are compared by their JSON form, which is enough for the scalars, id arrays
 * and small objects (`event_detail`) a work-item PATCH carries.
 */
function changedTaskFields(before: TIssue | undefined, data: Partial<TIssue>): string[] {
  return (Object.keys(data) as Array<keyof TIssue>).filter(
    (key) => !before || JSON.stringify(before[key] ?? null) !== JSON.stringify(data[key] ?? null),
  );
}

/** Props a caller may add to every event of one task write: only flags, so
 *  nothing but ids, enums and counts can reach the analytics sink. */
type TaskEventExtra = { undo?: boolean };

type TaskEventBase = {
  task_id: string;
  project_id: string | null;
  category: string | null;
} & TaskEventExtra;

/**
 * One `task_assigned` per member added and one `task_unassigned` per member
 * removed. A one-for-one swap is a reassignment: a single `task_assigned`
 * carrying `from_member_id`, and no `task_unassigned` (schema ruled on NG-2054).
 */
function trackAssigneeChanges(
  base: TaskEventBase,
  previous: string[],
  next: string[],
  source: AnalyticsSource,
): void {
  const added = next.filter((id) => !previous.includes(id));
  const removed = previous.filter((id) => !next.includes(id));
  if (added.length === 1 && removed.length === 1) {
    track("task_assigned", { ...base, member_id: added[0], from_member_id: removed[0], source });
    return;
  }
  for (const memberId of added) track("task_assigned", { ...base, member_id: memberId, source });
  for (const memberId of removed) {
    track("task_unassigned", { ...base, member_id: memberId, source });
  }
}

/**
 * Product analytics for one successful task write (NG-2056). `task_updated`
 * fires for every write that changed something, so edit counts are complete;
 * a status or assignee change also fires the lifecycle verb product asks
 * about. `source` names the surface the edit came from. Props are ids, enums
 * and counts only: never a title, a description or a name. `extra` joins every
 * event the write fires, such as `undo: true` for a toast's undo.
 */
export function trackTaskWrite(
  before: TIssue | undefined,
  saved: TIssue,
  data: Partial<TIssue>,
  source: AnalyticsSource,
  extra: TaskEventExtra = {},
): void {
  const fields = changedTaskFields(before, data);
  if (fields.length === 0) return;
  const base: TaskEventBase = {
    task_id: saved.id,
    project_id: saved.project_id ?? before?.project_id ?? null,
    category: saved.category ?? before?.category ?? null,
    ...extra,
  };
  track("task_updated", { ...base, fields, source });
  if (fields.includes("state_id")) {
    track("task_status_changed", {
      ...base,
      from: before?.state_id ?? null,
      to: data.state_id ?? null,
      source,
    });
  }
  if (fields.includes("assignee_ids")) {
    trackAssigneeChanges(base, before?.assignee_ids ?? [], data.assignee_ids ?? [], source);
  }
}

/** Props for a lifecycle event on one task, read from the row before the write. */
export function singleTaskProps(
  before: TIssue | undefined,
  issueId: string,
  projectId: string,
  source: AnalyticsSource,
): Record<string, unknown> {
  return {
    task_id: issueId,
    project_id: projectId,
    category: before?.category ?? null,
    source,
  };
}

/** What a bulk-bar action did to its tasks. */
type BulkTaskAction =
  | { kind: "deleted" }
  | { kind: "archived" }
  | { kind: "status"; to: string }
  | { kind: "assignee"; memberId: string; assign: boolean };

/**
 * One event for a bulk-bar action on tasks (NG-2056), carrying the server's
 * count of the tasks it changed and no task id: the schema's bulk form, ruled on
 * NG-2054, which the issue store's own bulk archive and delete already send. A status or assignee change also
 * fires `task_updated`, so edit counts stay complete, as a single write's do. An
 * action that changed nothing, all skipped or cancelled first, fires nothing.
 * `scope` adds the project when the bulk bar is a project's.
 */
export function trackBulkTaskWrite(
  action: BulkTaskAction,
  count: number,
  scope: { project_id?: string } = {},
): void {
  if (count <= 0) return;
  const base = { ...scope, count, source: "bulk" as AnalyticsSource };
  switch (action.kind) {
    case "deleted":
      track("task_deleted", base);
      return;
    case "archived":
      track("task_archived", base);
      return;
    case "status":
      track("task_updated", { ...base, fields: ["state_id"] });
      track("task_status_changed", { ...base, to: action.to });
      return;
    case "assignee":
      track("task_updated", { ...base, fields: ["assignee_ids"] });
      track(action.assign ? "task_assigned" : "task_unassigned", {
        ...base,
        member_id: action.memberId,
      });
      return;
  }
}

/**
 * A custom field value written through the custom-fields service, which the
 * issue store never sees (NG-2056): `task_updated` naming the field by its id,
 * never its label, like any other field.
 */
export function trackTaskCustomFieldWrite(
  task: Pick<TIssue, "id" | "project_id" | "category">,
  fieldId: string,
  source: AnalyticsSource,
): void {
  track("task_updated", {
    task_id: task.id,
    project_id: task.project_id ?? null,
    category: task.category ?? null,
    fields: [fieldId],
    source,
  });
}
